import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ArrowLeft, Check, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DEFAULT_PRODUCT_IMAGE } from "@/lib/productImage";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { FadeUp } from "@/components/Reveal";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, items } = useCart();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notifyState, setNotifyState] = useState("idle"); // idle | loading | signed

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotifyState("idle");
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      if (data.in_stock === false) {
        api.get(`/products/${id}/restock-signup`)
          .then(({ data: r }) => { if (r.signed_up) setNotifyState("signed"); })
          .catch(() => {});
      }
    }).catch(() => navigate("/shop"));
  }, [id, navigate]);

  const handleAdd = () => {
    const inCart = items.find((i) => i.product_id === product.id)?.quantity || 0;
    if (inCart + qty > product.stock) {
      toast.error(`Only ${product.stock} unit${product.stock === 1 ? "" : "s"} of ${product.name} in stock${inCart ? ` (${inCart} already in your cart)` : ""}`);
      return;
    }
    addItem(product, qty);
    toast.success(`${qty} × ${product.name} added to cart`);
  };

  const handleNotify = async () => {
    setNotifyState("loading");
    try {
      await api.post(`/products/${id}/restock-signup`);
      setNotifyState("signed");
      toast.success("You're on the restock list");
    } catch {
      setNotifyState("idle");
      toast.error("Could not add you to the restock list");
    }
  };

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center"><span className="label text-muted-foreground animate-pulse">Loading…</span></div>;
  }

  const available = product.in_stock !== false && (product.stock ?? 0) > 0;

  return (
    <main className="pt-28 md:pt-32 pb-24" data-testid="product-detail-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Link to="/shop" className="inline-flex items-center gap-2 label text-muted-foreground hover:text-foreground transition-colors mb-10" data-testid="back-to-shop">
          <ArrowLeft size={16} strokeWidth={1.5} /> Back to shop
        </Link>

        <div className="grid md:grid-cols-2 gap-10 md:gap-20">
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[3/4] overflow-hidden border border-border bg-secondary"
          >
            <img src={DEFAULT_PRODUCT_IMAGE} alt={product.name} className={`h-full w-full object-contain bg-white p-8${available ? "" : " grayscale opacity-50"}`} />
            {!available && (
              <div className="absolute top-0 right-0 p-4">
                <span className="label bg-background/80 backdrop-blur px-2 py-1 text-destructive" data-testid="sold-out-badge">Sold Out</span>
              </div>
            )}
          </motion.div>

          <div className="flex flex-col">
            <FadeUp>
              <p className="label text-accent mb-5">/ {product.category}</p>
              <h1 className="font-display text-5xl md:text-6xl font-light leading-[0.95] tracking-tight">{product.name}</h1>
              <p className="mt-4 text-sm text-muted-foreground">{product.tagline}</p>

              <div className="mt-6 flex items-center gap-4">
                <span className="font-mono text-2xl">${product.price.toFixed(2)}</span>
              </div>

              <p className="mt-8 text-sm leading-relaxed text-foreground/80 max-w-md">{product.description}</p>
            </FadeUp>

            {product.benefits?.length > 0 && (
              <FadeUp delay={0.1}>
                <div className="mt-8 flex flex-wrap gap-2">
                  {product.benefits.map((b) => (
                    <span key={b} className="inline-flex items-center gap-1.5 label px-3 py-1.5 border border-border text-foreground/70">
                      <Check size={12} strokeWidth={2} className="text-accent" /> {b}
                    </span>
                  ))}
                </div>
              </FadeUp>
            )}

            {product.ingredients?.length > 0 && (
              <FadeUp delay={0.15}>
                <div className="mt-8 border-t border-border pt-6">
                  <p className="label text-muted-foreground mb-3">Composition</p>
                  <ul className="text-sm text-foreground/80 space-y-1">
                    {product.ingredients.map((ing) => (
                      <li key={ing} className="font-mono">— {ing}</li>
                    ))}
                  </ul>
                </div>
              </FadeUp>
            )}

            <FadeUp delay={0.2}>
              {available ? (
                <div className="mt-10">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center border border-border" data-testid="qty-selector">
                      <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-14 w-14 flex items-center justify-center hover:bg-secondary transition-colors" data-testid="qty-decrease" aria-label="Decrease quantity">
                        <Minus size={16} strokeWidth={1.5} />
                      </button>
                      <span className="w-12 text-center font-mono text-sm" data-testid="qty-value">{qty}</span>
                      <button onClick={() => setQty((q) => Math.min(product.stock, q + 1))} disabled={qty >= product.stock} className="h-14 w-14 flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-testid="qty-increase" aria-label="Increase quantity">
                        <Plus size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={handleAdd}
                      className="flex-1 h-14 label bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-300"
                      data-testid="add-to-cart-btn"
                    >
                      Add to cart — ${(product.price * qty).toFixed(2)}
                    </motion.button>
                  </div>
                  {product.stock <= 10 && (
                    <p className="label text-muted-foreground mt-3" data-testid="stock-hint">Only {product.stock} left in stock</p>
                  )}
                </div>
              ) : (
                <div className="mt-10 border border-border p-6 md:p-8" data-testid="restock-notify-block">
                  <p className="label text-destructive mb-3">Sold out</p>
                  <p className="text-sm text-foreground/80 mb-6 max-w-md">
                    This compound is temporarily unavailable. Join the restock list and we'll email
                    {user?.email ? <> <span className="font-mono">{user.email}</span> </> : " you "}
                    the moment it's back.
                  </p>
                  <button
                    onClick={handleNotify}
                    disabled={notifyState !== "idle"}
                    className={`label inline-flex items-center gap-2 px-6 py-3.5 transition-colors ${notifyState === "signed" ? "bg-secondary text-muted-foreground cursor-default" : "bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"}`}
                    data-testid="restock-notify-btn"
                  >
                    {notifyState === "signed" ? <Check size={16} strokeWidth={1.5} /> : <Bell size={16} strokeWidth={1.5} />}
                    {notifyState === "signed" ? "You're on the list" : notifyState === "loading" ? "Joining…" : "Notify me when available"}
                  </button>
                </div>
              )}
            </FadeUp>
          </div>
        </div>
      </div>
    </main>
  );
}
